import { useRouter } from "@shared/client/navigation";
import { useState, useTransition } from "react";
import { Search } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";

const LEVELS = [
  { value: "all", label: "Tous les niveaux" },
  { value: "info", label: "Info" },
  { value: "warn", label: "Avertissement" },
  { value: "error", label: "Erreur" },
];

const SOURCES = [
  { value: "all", label: "Toutes les sources" },
  { value: "api", label: "API" },
  { value: "db", label: "Base de données" },
  { value: "auth", label: "Auth" },
  { value: "external", label: "Externe" },
  { value: "app", label: "Application" },
];

/**
 * Filtres des logs : les listes déroulantes appliquent immédiatement, la
 * recherche attend la validation. L'état vit dans l'URL — un filtre se partage.
 */
export function LogFilters({ level, source, q }: { level: string; source: string; q: string }) {
  const router = useRouter();
  const [, startTransition] = useTransition();
  const [search, setSearch] = useState(q);

  function apply(next: { level?: string; source?: string; q?: string }) {
    const params = new URLSearchParams();
    const merged = { level, source, q: search, ...next };
    if (merged.level && merged.level !== "all") params.set("level", merged.level);
    if (merged.source && merged.source !== "all") params.set("source", merged.source);
    if (merged.q) params.set("q", merged.q);
    startTransition(() => router.push(`/admin/logs${params.size ? `?${params}` : ""}`));
  }

  return (
    <form
      className="flex flex-1 flex-wrap items-center gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        apply({});
      }}
    >
      <div className="relative min-w-[220px] flex-1">
        <Search aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="h-9 pl-9"
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Opération, chemin, message d’erreur…"
          value={search}
        />
      </div>

      <Select onValueChange={(value) => apply({ level: value })} value={level}>
        <SelectTrigger aria-label="Niveau" className="h-9 text-xs font-semibold">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LEVELS.map((item) => (
            <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select onValueChange={(value) => apply({ source: value })} value={source}>
        <SelectTrigger aria-label="Source" className="h-9 text-xs font-semibold">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SOURCES.map((item) => (
            <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button size="sm" type="submit" variant="outline">Filtrer</Button>
    </form>
  );
}
